import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconClose } from './icons.js';

type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  title: string;
  message?: string;
}

interface ToastApi {
  /** Sucesso de uma ação (baixa emitida, config salva). */
  success: (title: string, message?: string) => void;
  /** Falha de uma ação — fica mais tempo na tela. */
  error: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DURACAO: Record<ToastKind, number> = { success: 4000, info: 5000, error: 8000 };

/**
 * Provedor de avisos transitórios.
 *
 * Confirma o resultado de uma escrita sem interromper o fluxo. Erro dura mais
 * porque quem opera cobrança precisa de tempo para ler o motivo da falha.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [itens, setItens] = useState<ToastItem[]>([]);
  const proximoId = useRef(1);

  const fechar = useCallback((id: number) => {
    setItens((atual) => atual.filter((t) => t.id !== id));
  }, []);

  const empurrar = useCallback(
    (kind: ToastKind, title: string, message?: string) => {
      const id = proximoId.current++;
      setItens((atual) => [...atual, { id, kind, title, ...(message ? { message } : {}) }]);
      setTimeout(() => fechar(id), DURACAO[kind]);
    },
    [fechar],
  );

  const api = useRef<ToastApi>({
    success: (t, m) => empurrar('success', t, m),
    error: (t, m) => empurrar('error', t, m),
    info: (t, m) => empurrar('info', t, m),
  }).current;

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="toaster" aria-live="polite">
          {itens.map((t) => (
            <div key={t.id} className={`toast toast--${t.kind}`} role="status">
              <span className="toast__bar" />
              <div className="toast__body">
                <div className="toast__title">{t.title}</div>
                {t.message && <div className="toast__msg">{t.message}</div>}
              </div>
              <button type="button" className="toast__x" aria-label="Fechar aviso" onClick={() => fechar(t.id)}>
                <IconClose size={15} />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast fora do ToastProvider');
  return ctx;
}
